/**
 * Wave 4 / FR-14 / HUX-05 / R10 — full-width promo hero.
 * Collapses (0px) when incomplete: missing media, missing/unresolvable CTA, or media onError.
 * Media HOW: resolved stream URL from bannerStorageKey (never bannerImageUrl).
 * Overlay title/subtitle → contrast scrim REQUIRED (`browse-home-promo-scrim`).
 */

import { useState } from 'react';
import type { PublicDiscoveryHomeBannerTargetType } from '../discovery/publicDiscoveryTypes.js';
import { resolveBrowseHomePromoCta } from './resolveBrowseHomePromoCta.js';

export type BrowseHomePromoBannerProps = {
  readonly title: string | null | undefined;
  readonly subtitle: string | null | undefined;
  /** Preferred CTA label prop. */
  readonly ctaLabel?: string | null | undefined;
  /** Alias for ctaLabel (Wave 4 call sites). */
  readonly cta?: string | null | undefined;
  readonly imageUrl: string | null | undefined;
  readonly targetType?: PublicDiscoveryHomeBannerTargetType | null | undefined;
  readonly targetRef?: string | null | undefined;
  readonly onCtaClick?: () => void;
  readonly className?: string;
};

function trimText(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/**
 * Actionable completeness (FR-14): title + media URL + CTA label + resolvable target + click handler.
 * When targetType is omitted, require onCtaClick only (caller already validated the action).
 */
export function isBrowseHomePromoComplete(props: BrowseHomePromoBannerProps): boolean {
  const title = trimText(props.title);
  const imageUrl = trimText(props.imageUrl);
  const ctaLabel = trimText(props.ctaLabel ?? props.cta);
  if (title.length === 0 || imageUrl.length === 0 || ctaLabel.length === 0) {
    return false;
  }
  if (typeof props.onCtaClick !== 'function') {
    return false;
  }
  if (props.targetType !== undefined && props.targetType !== null) {
    const action = resolveBrowseHomePromoCta({
      targetType: props.targetType,
      targetRef: props.targetRef ?? null,
    });
    if (action === null) {
      return false;
    }
  }
  return true;
}

export function BrowseHomePromoBanner(props: BrowseHomePromoBannerProps): JSX.Element | null {
  const imageUrl = trimText(props.imageUrl);
  /** Failed URL identity — when `imageUrl` changes, failure auto-clears (no effect). */
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const mediaFailed = failedImageUrl !== null && failedImageUrl === imageUrl;

  if (mediaFailed || !isBrowseHomePromoComplete(props)) {
    return null;
  }

  const title = trimText(props.title);
  const subtitle = trimText(props.subtitle);
  const ctaLabel = trimText(props.ctaLabel ?? props.cta);
  /**
   * Completeness requires title → overlay copy always sits on media when mounted.
   * R10: contrast scrim is REQUIRED (never mount text on raw media).
   */
  return (
    <aside
      className={[
        'relative w-full overflow-hidden',
        'rounded-[0.8125rem]',
        // G5: aspect alone (no min-h / no max-h) keeps ~3.1:1 at 320/375/390/430
        'aspect-[3.1/1]',
        props.className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="customer-browse-home-promo"
    >
      <img
        src={imageUrl}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        data-testid="customer-browse-home-promo-media"
        onError={() => {
          setFailedImageUrl(imageUrl);
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"
        data-testid="browse-home-promo-scrim"
        aria-hidden="true"
      />
      <div className="relative z-[1] flex h-full min-h-0 flex-col justify-end px-4 pb-3 pt-6">
        <div className="min-w-0 max-w-[85%] space-y-0.5 text-left">
          <h2
            className="truncate text-base font-semibold text-white"
            data-testid="customer-browse-home-promo-title"
          >
            {title}
          </h2>
          {subtitle.length > 0 ? (
            <p
              className="line-clamp-2 text-sm text-white/90"
              data-testid="customer-browse-home-promo-subtitle"
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={[
            'mt-2 inline-flex min-h-11 items-center self-start rounded-full',
            'bg-white px-4 text-[13px] font-medium leading-none text-[var(--color-on-surface)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
            'focus-visible:outline-[var(--color-focus-ring)]',
          ].join(' ')}
          data-testid="customer-browse-home-promo-cta"
          onClick={props.onCtaClick}
        >
          {ctaLabel}
        </button>
      </div>
    </aside>
  );
}
